import { DATE_FORMAT_LOCALE, DATE_FORMAT_OPTIONS, DATE_TIME_FORMAT_OPTIONS, CURRENCY_CODE, CURRENCY_SYMBOL } from '../styles/theme/theme';
import { useUserStore } from '../../app/store/useUserStore';

// Fallback multiplier used until useGetWeightUnitsQuery has populated the store (e.g. before login).
const FALLBACK_KG_MULTIPLIER_FROM_LB = 0.453592;

// Prices are always USD, returned directly by the API — no currency conversion needed.
export type Currency = 'USD';

export type WeightUnit = 'kg' | 'lbs';

interface LocalizationConfig {
  currency: Currency;
  locale: string;
  timeZone: string;
  isMetric: boolean;
}

const US_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'America/Honolulu',
];

class LocalizationService {
  private static instance: LocalizationService;
  private config: LocalizationConfig;

  private constructor() {
    const detectedTimeZone = this.detectTimeZone();
    const detectedCurrency = this.getCurrencyFromTimeZone(detectedTimeZone);
    const detectedLocale = this.getLocaleFromTimeZone(detectedTimeZone);
    const isMetric = !US_TIMEZONES.includes(detectedTimeZone);

    this.config = {
      currency: detectedCurrency,
      locale: detectedLocale,
      timeZone: detectedTimeZone,
      isMetric,
    };
  }

  public static getInstance(): LocalizationService {
    if (!LocalizationService.instance) {
      LocalizationService.instance = new LocalizationService();
    }
    return LocalizationService.instance;
  }

  private detectTimeZone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return 'America/New_York';
    }
  }

  private getCurrencyFromTimeZone(_timeZone: string): Currency {
    // Prices are always USD, returned directly by the API — no per-region currency detection needed.
    return 'USD';
  }

  private getLocaleFromTimeZone(timeZone: string): string {
    if (timeZone.startsWith('America/') && US_TIMEZONES.includes(timeZone)) {
      return 'en-US';
    }
    if (timeZone.startsWith('Europe/London')) return 'en-GB';
    if (timeZone.startsWith('Europe/Paris')) return 'fr-FR';
    if (timeZone.startsWith('Europe/Berlin')) return 'de-DE';
    if (timeZone.startsWith('Europe/Madrid')) return 'es-ES';
    if (timeZone.startsWith('Europe/Rome')) return 'it-IT';
    if (timeZone.startsWith('Asia/Tokyo')) return 'ja-JP';
    if (timeZone.startsWith('Asia/Shanghai')) return 'zh-CN';
    if (timeZone.startsWith('Australia/')) return 'en-AU';

    return 'en-US';
  }

  // Prices are always USD, returned directly by the API — no conversion needed.
  public convertFromUSD(amountUSD: number): number {
    return amountUSD;
  }

  public formatCurrency(amount: number, _convertCurrency: boolean = true): string {
    return this.formatLocalCurrency(amount);
  }

  public formatLocalCurrency(amount: number): string {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: CURRENCY_CODE,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      return `${CURRENCY_SYMBOL}${amount.toFixed(2)}`;
    }
  }

  public getCurrencySymbol(): string {
    return CURRENCY_SYMBOL;
  }

  // Multiplier to convert 1 lb into kg, sourced from useGetWeightUnitsQuery (cached in useUserStore)
  // once available; falls back to the standard conversion factor until then.
  private getKgMultiplierFromLb(): number {
    const kgUnit = useUserStore.getState().weightUnits.find((u) => u.code === 'kg');
    return kgUnit?.multiplierFromLb || FALLBACK_KG_MULTIPLIER_FROM_LB;
  }

  public lbsToKg(lbs: number): number {
    return lbs * this.getKgMultiplierFromLb();
  }


  public kgToLbs(kg: number): number {
    return kg / this.getKgMultiplierFromLb();
  }

  public formatWeight(weightLbs: number): {
    display: string;
    value: number;
    unit: WeightUnit;
    mainValue: string;
    secondaryValue: string;
  } {
    const kg = this.lbsToKg(weightLbs);
    const primaryUnit = this.getWeightUnit();

    if (primaryUnit === 'kg') {
      return {
        display: `${kg.toFixed(2)} kg (${weightLbs.toFixed(2)} lbs)`,
        value: kg,
        unit: 'kg',
        mainValue: `${kg.toFixed(2)} kg`,
        secondaryValue: `(${weightLbs.toFixed(2)} lbs)`,
      };
    }

    return {
      display: `${weightLbs.toFixed(2)} lbs (${kg.toFixed(2)} kg)`,
      value: weightLbs,
      unit: 'lbs',
      mainValue: `${weightLbs.toFixed(2)} lbs`,
      secondaryValue: `(${kg.toFixed(2)} kg)`,
    };
  }

 
  public formatWeightWithBothUnits(weightLbs: number): string {
    const kg = this.lbsToKg(weightLbs);
    return `${weightLbs.toFixed(2)} lbs (${kg.toFixed(2)} kg)`;
  }

  public convertLbsToKg(lbs: number): number {
    return this.lbsToKg(lbs);
  }

  // Returns price per local weight unit (per kg in metric regions, per lb otherwise).
  public calculatePricePerLb(price: number, weightLbs: number): number | null {
    if (!weightLbs || weightLbs <= 0 || !price || price <= 0) {
      return null;
    }
    const weightInLocalUnit = this.getWeightUnit() === 'kg' ? this.lbsToKg(weightLbs) : weightLbs;
    return price / weightInLocalUnit;
  }

  public formatPricePerLb(price: number, weightLbs: number): string | null {
    const pricePerLb = this.calculatePricePerLb(price, weightLbs);
    if (pricePerLb === null) {
      return null;
    }
    return this.formatLocalCurrency(pricePerLb);
  }

 
  public convertWeight(value: number, fromUnit: WeightUnit, toUnit: WeightUnit): number {
    if (fromUnit === toUnit) return value;
    if (fromUnit === 'kg' && toUnit === 'lbs') return this.kgToLbs(value);
    if (fromUnit === 'lbs' && toUnit === 'kg') return this.lbsToKg(value);
    return value;
  }

  public getWeightUnit(): WeightUnit {
    return this.config.isMetric ? 'kg' : 'lbs';
  }

  public formatWeightRange(minLbs: number, maxLbs: number): { min: number; max: number; unit: WeightUnit } {
    const unit = this.getWeightUnit();
    if (unit === 'kg') {
      return { min: Math.round(this.lbsToKg(minLbs)), max: Math.round(this.lbsToKg(maxLbs)), unit };
    }
    return { min: minLbs, max: maxLbs, unit };
  }

  public getConfig(): LocalizationConfig {
    return { ...this.config };
  }

  public getCurrency(): Currency {
    return this.config.currency;
  }

  public getLocale(): string {
    return this.config.locale;
  }

  public getTimeZone(): string {
    return this.config.timeZone;
  }

  public isMetric(): boolean {
    return this.config.isMetric;
  }

  public getDateFormat(): string {
    const monthFirstLocales = ['en-US', 'en-CA'];
    const dotSeparatorLocales = ['de-DE', 'de-AT', 'de-CH'];

    if (monthFirstLocales.includes(this.config.locale)) {
      return 'MM/DD/YYYY';
    }

    if (dotSeparatorLocales.includes(this.config.locale)) {
      return 'DD.MM.YYYY';
    }

    return 'DD/MM/YYYY';
  }

 
  public getTimeFormat(): '12h' | '24h' {
    const hour12Locales = ['en-US', 'en-CA', 'en-AU', 'en-NZ', 'en-PH'];

    return hour12Locales.includes(this.config.locale) ? '12h' : '24h';
  }

  public formatDateForInput(date: Date | null): string {
    if (!date) return '';
    return new Intl.DateTimeFormat(DATE_FORMAT_LOCALE, DATE_FORMAT_OPTIONS).format(date);
  }

 
  public formatTimeForInput(hour24: number, minute: number): string {
    const minuteStr = minute.toString().padStart(2, '0');
    const format = this.getTimeFormat();

    if (format === '12h') {
      const period = hour24 >= 12 ? 'PM' : 'AM';
      let hour12 = hour24 % 12;
      if (hour12 === 0) hour12 = 12;
      return `${hour12.toString().padStart(2, '0')}:${minuteStr} ${period}`;
    } else {
      return `${hour24.toString().padStart(2, '0')}:${minuteStr}`;
    }
  }

  public getDatePlaceholder(): string {
    return 'MMM DD, YYYY';
  }

  public getTimePlaceholder(): string {
    const format = this.getTimeFormat();
    return format === '12h' ? 'HH:MM AM/PM' : 'HH:MM';
  }

  public formatDate(date: Date | string, options?: Intl.DateTimeFormatOptions): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const resolvedOptions: Intl.DateTimeFormatOptions = {
      timeZone: this.config.timeZone,
      ...DATE_FORMAT_OPTIONS,
      ...options,
    };

    try {
      return new Intl.DateTimeFormat(DATE_FORMAT_LOCALE, resolvedOptions).format(dateObj);
    } catch {
      return new Intl.DateTimeFormat(DATE_FORMAT_LOCALE, DATE_FORMAT_OPTIONS).format(dateObj);
    }
  }

  public formatDateTime(date: Date | string, options?: Intl.DateTimeFormatOptions): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const resolvedOptions: Intl.DateTimeFormatOptions = {
      timeZone: this.config.timeZone,
      ...DATE_TIME_FORMAT_OPTIONS,
      ...options,
    };

    try {
      return new Intl.DateTimeFormat(DATE_FORMAT_LOCALE, resolvedOptions).format(dateObj);
    } catch {
      return new Intl.DateTimeFormat(DATE_FORMAT_LOCALE, DATE_TIME_FORMAT_OPTIONS).format(dateObj);
    }
  }
}

export default LocalizationService;
